import { useApolloClient } from "@apollo/client";
import { useTranslate } from "@tolgee/react";
import { getDataOrNull } from "@msys/common";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import {
  IntegrationId,
  namedOperations,
} from "../../../../../clients/graphqlTypes.js";
import { useGenericDashboardQuery } from "../GenericDashboard.generated.js";
import { INTEGRATIONS_BASE_PATH } from "../../paths.js";
import {
  useIntegrationSettings_AddMutation,
  useIntegrationSettings_UpdateMutation,
  useIntegrationSettings_RemoveMutation,
} from "./IntegrationSettings.generated.js";

export function useIntegrationSettingsBase(
  integrationId: string,
  mode: "create" | "edit"
) {
  const { t } = useTranslate("Integrations");
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const client = useApolloClient();

  const { data, loading, refetch } = useGenericDashboardQuery({
    client,
    variables: { integrationId: integrationId as IntegrationId },
    fetchPolicy: "cache-and-network",
    skip: mode === "create",
  });

  const orgIntegration = getDataOrNull(
    data?.organisationIntegration
  )?.organisationIntegration;

  const [addMutation] = useIntegrationSettings_AddMutation({
    client,
    context: { noErrorNotification: true },
  });
  const [updateMutation] = useIntegrationSettings_UpdateMutation({
    client,
    context: { noErrorNotification: true },
  });
  const [removeMutation, { loading: removeLoading }] =
    useIntegrationSettings_RemoveMutation({
      client,
      context: { noErrorNotification: true },
    });

  const handleAdd = async (settings?: any, foreignAccountId?: string) => {
    await addMutation({
      variables: {
        input: {
          integrationId: integrationId as IntegrationId,
          // null (not {}) — plugins apply their create defaults only for a
          // missing settings object; {} is truthy and fails schemas with
          // required fields
          settings: settings ?? null,
          foreignAccountId: foreignAccountId ?? null,
        },
      },
      refetchQueries: [
        namedOperations.Query.IntegrationsOverview,
        namedOperations.Query.OrganisationIntegrationsSidebar,
      ],
    });
    navigate(INTEGRATIONS_BASE_PATH);
  };

  const handleUpdate = async (settings: any) => {
    await updateMutation({
      variables: {
        input: {
          integrationId: integrationId as IntegrationId,
          settings,
        },
      },
    });
    enqueueSnackbar(t("Settings saved"), { variant: "success" });
  };

  const handleDisconnect = async () => {
    await removeMutation({
      variables: {
        input: { integrationId: integrationId as IntegrationId },
      },
      refetchQueries: [
        namedOperations.Query.IntegrationsOverview,
        namedOperations.Query.OrganisationIntegrationsSidebar,
      ],
      awaitRefetchQueries: true,
    });
    navigate(INTEGRATIONS_BASE_PATH);
  };

  return {
    client,
    orgIntegration,
    loading,
    refetchDashboard: refetch,
    removeLoading,
    handleAdd,
    handleUpdate,
    handleDisconnect,
    enqueueSnackbar,
    navigate,
  };
}
